<template>
  <div class="address-management">
    <div class="header">
      <h3>地址管理</h3>
      <el-button type="primary" size="small" :disabled="editDisabled" @click="showCreateDialog()">
        <el-icon><Plus /></el-icon> 新增地址
      </el-button>
    </div>

    <el-table :data="addresses" v-loading="loading" size="small">
      <el-table-column prop="address_type" label="地址类型" width="120">
        <template #default="{ row }">
          <el-tag :type="getAddressTypeTag(row.address_type)">
            {{ getAddressTypeText(row.address_type) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="recipient_name" label="收件人" width="120" />
      <el-table-column prop="recipient_phone" label="联系电话" width="130" />
      <el-table-column prop="address_line" label="详细地址" min-width="200" show-overflow-tooltip />
      <el-table-column prop="is_default" label="默认地址" width="100">
        <template #default="{ row }">
          <el-tag v-if="row.is_default" type="success" size="small">是</el-tag>
          <span v-else>-</span>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="150" fixed="right">
        <template #default="{ row }">
          <el-button size="small" :disabled="editDisabled" @click="handleEdit(row)">编辑</el-button>
          <el-button size="small" type="danger" :disabled="editDisabled" @click="handleDelete(row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 新增/编辑地址对话框 -->
    <el-dialog
      :title="isEditing ? '编辑地址' : '新增地址'"
      v-model="showFormDialog"
      width="500px"
    >
      <el-form
        :model="formData"
        :rules="formRules"
        ref="formRef"
        label-width="100px"
      >
        <el-form-item label="地址类型" prop="address_type">
          <el-select v-model="formData.address_type" placeholder="请选择地址类型">
            <el-option label="公司地址" value="company" />
            <el-option label="快递地址" value="shipping" />
            <el-option label="交货地址" value="delivery" />
            <el-option label="其他地址" value="other" />
          </el-select>
        </el-form-item>
        <el-form-item label="收件人">
          <el-input v-model="formData.recipient_name" placeholder="请输入收件人姓名" />
        </el-form-item>
        <el-form-item label="联系电话">
          <el-input v-model="formData.recipient_phone" placeholder="请输入联系电话" />
        </el-form-item>
        <el-form-item label="详细地址" prop="address_line">
          <el-input
            v-model="formData.address_line"
            type="textarea"
            placeholder="请输入详细地址"
            :rows="3"
          />
        </el-form-item>
        <el-form-item label="默认地址">
          <el-switch v-model="formData.is_default" />
        </el-form-item>
      </el-form>

      <template #footer>
        <span class="dialog-footer">
          <el-button @click="showFormDialog = false">取消</el-button>
          <el-button type="primary" @click="submitForm">确认</el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, watch } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Plus } from '@element-plus/icons-vue'
import { 
  getCustomerAddresses, 
  createCustomerAddress, 
  updateCustomerAddress, 
  deleteCustomerAddress 
} from '@/api/customers'

const props = defineProps({
  customerId: {
    type: Number,
    required: true
  },
  /** 客户主档锁定后禁止增删改地址 */
  editDisabled: {
    type: Boolean,
    default: false
  }
})

const loading = ref(false)
const addresses = ref([])
const showFormDialog = ref(false)
const isEditing = ref(false)
const formRef = ref()

const formData = reactive({
  address_type: '',
  recipient_name: '',
  recipient_phone: '',
  address_line: '',
  is_default: false
})

const formRules = {
  address_type: [
    { required: true, message: '请选择地址类型', trigger: 'change' }
  ],
  address_line: [
    { required: true, message: '请输入详细地址', trigger: 'blur' }
  ]
}

// 监听customerId变化
watch(() => props.customerId, (newVal) => {
  if (newVal) {
    loadAddresses()
  }
})

// 地址类型标签映射
const getAddressTypeTag = (type) => {
  const tags = {
    company: 'primary',
    shipping: 'success',
    delivery: 'warning',
    other: 'info'
  }
  return tags[type] || 'info'
}

// 地址类型文本映射
const getAddressTypeText = (type) => {
  const texts = {
    company: '公司地址',
    shipping: '快递地址',
    delivery: '交货地址',
    other: '其他地址'
  }
  return texts[type] || type
}

// 加载地址数据
const loadAddresses = async () => {
  if (!props.customerId) return
  
  loading.value = true
  try {
    const response = await getCustomerAddresses(props.customerId)
    addresses.value = response.data
  } catch (error) {
    ElMessage.error('加载地址失败')
  } finally {
    loading.value = false
  }
}

// 打开新增对话框
const showCreateDialog = () => {
  isEditing.value = false
  resetForm()
  showFormDialog.value = true
}

// 打开编辑对话框
const handleEdit = (row) => {
  isEditing.value = true
  Object.assign(formData, row)
  showFormDialog.value = true
}

// 删除地址
const handleDelete = async (row) => {
  try {
    await ElMessageBox.confirm(
      `确定要删除这个地址吗？`,
      '警告',
      { type: 'warning' }
    )
    
    await deleteCustomerAddress(props.customerId, row.address_id)
    ElMessage.success('地址删除成功')
    loadAddresses()
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error('删除失败')
    }
  }
}

// 提交表单
const submitForm = async () => {
  if (!formRef.value) return

  try {
    await formRef.value.validate()
    
    if (isEditing.value) {
      await updateCustomerAddress(props.customerId, formData.address_id, formData)
      ElMessage.success('地址更新成功')
    } else {
      await createCustomerAddress(props.customerId, formData)
      ElMessage.success('地址创建成功')
    }
    
    showFormDialog.value = false
    loadAddresses()
  } catch (error) {
    if (error.response?.data?.message) {
      ElMessage.error(error.response?.message)
    }
  }
}

// 重置表单
const resetForm = () => {
  if (formRef.value) {
    formRef.value.resetFields()
  }
  Object.assign(formData, {
    address_type: '',
    recipient_name: '',
    recipient_phone: '',
    address_line: '',
    is_default: false
  })
}

// 初始化加载
onMounted(() => {
  if (props.customerId) {
    loadAddresses()
  }
})

// 暴露方法给父组件
defineExpose({
  showCreateDialog
})
</script>

<style scoped>
.address-management {
  padding: 10px;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.header h3 {
  margin: 0;
  font-size: 16px;
  color: #303133;
}
</style>